import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Button } from '@one-thd/sui-atomic-components';
import { VideoPlayer } from '@thd-olt-component-react/video-player';
import { SweepstakesMarkdown } from '../../../utils/SweepstakesMarkdown';
import { getStepVideoConfig } from '../../../utils/step-video-config';
import { SWEEPSTAKES_CONSTANTS, VIDEO_ANALYTICS } from '../../../../../core/Constants';
import { analyticsVideo } from '../../../../../common/utils/AnalyticsUtils';
import { windowExists } from '../../../../../common/utils/AccountUtils';
import { useResponsive } from '../../../../../hooks/use-responsive';
import { useStepVideoCompletion } from '../../../../../hooks/use-step-video-completion';
import { useSweepstakesData } from '../../../../../contexts/SweepstakesDataContext';
import { handleAlertStyles } from '../../../../../common/utils/DrawerUtils';
import {
  SWEEPSTAKES_NR_CTA,
  trackSweepstakesCtaClick,
} from '../../../../../common/utils/NewRelic';

const FALLBACK_VIDEO = {
  videoId: '6390093564112',
  shouldUseContentPlayer: true,
};

// Find the first step across all steps that has explicit video metadata (brightcoveVideoId / videoId).
const findVideoStep = (steps) => {
  if (!Array.isArray(steps)) return null;
  return steps.find((step) => Boolean(getStepVideoConfig(step))) || null;
};

const getStepPosition = (step, steps) => {
  if (!step || !Array.isArray(steps)) {
    if (typeof step?.stepIndex === 'number') return step.stepIndex;
    return -1;
  }
  const idx = steps.findIndex(
    (candidate) => (step.id && candidate.id === step.id)
      || (step.title && candidate.title === step.title)
  );
  if (idx !== -1) return idx;
  if (typeof step?.stepIndex === 'number') return step.stepIndex;
  return -1;
};

const resolveVideoConfig = (step, steps) => {
  const directConfig = getStepVideoConfig(step);
  if (directConfig) return directConfig;

  const videoStep = findVideoStep(steps);

  if (videoStep) {
    const isCurrent = step?.id ? step.id === videoStep.id : step?.title === videoStep.title;
    return isCurrent ? getStepVideoConfig(videoStep) : null;
  }

  const position = getStepPosition(step, steps);
  if (position !== 1) return null;

  const secondStep = steps?.[1];
  const fallbackVideoId = secondStep?.stepMetaData?.brightcoveVideoId
    || FALLBACK_VIDEO.videoId;
  return {
    videoId: fallbackVideoId,
    shouldUseContentPlayer: FALLBACK_VIDEO.shouldUseContentPlayer,
  };
};

const RenderDescription = ({ description }) => {
  if (!description) return null;

  return (
    <div className="sui-mb-4">
      <SweepstakesMarkdown markdownText={description} variant="stepContent" />
    </div>
  );
};

const RenderStepVideo = ({ videoConfig, infoText }) => {
  const alertRef = React.useRef(null);

  React.useEffect(() => {
    handleAlertStyles(alertRef);
  }, [videoConfig]);

  if (!videoConfig) return null;

  return (
    <div data-testid="sweepstakes-step-video" className="sui-w-full">
      <VideoPlayer
        videoId={videoConfig.videoId}
        shouldUseContentPlayer={videoConfig.shouldUseContentPlayer}
      />
      {infoText && (
        <div className="sui-mt-4">
          <Alert ref={alertRef} status="info" data-testid="sweepstakes-step-video-info">
            {infoText}
          </Alert>
        </div>
      )}
    </div>
  );
};

const RenderFooter = ({ showDrawerAppCta, ctaTitle, onClick, disabled }) => {
  if (!showDrawerAppCta || !ctaTitle) return null;

  return (
    <Button
      variant="primary"
      fullWidth
      onClick={onClick}
      disabled={disabled}
      data-testid="sweepstakes-step-cta-button"
    >
      {ctaTitle}
    </Button>
  );
};

export const StepContent = ({ step }) => {
  const { isMobile } = useResponsive();
  const {
    sweepstakesData,
    updateSweepstakesData,
    completeSweepstakesVideoStep,
    isLoadingCompleteVideo,
  } = useSweepstakesData();
  const steps = sweepstakesData?.steps;
  const videoConfig = resolveVideoConfig(step, steps);

  const { videoStarted } = useStepVideoCompletion({
    step,
    videoConfig,
    sweepstakesData,
    updateSweepstakesData,
    completeSweepstakesVideoStep,
    isLoadingCompleteVideo,
  });

  if (!step) return { content: null, footer: null };

  const handleCtaClick = () => {
    if (!windowExists()) return;

    const { ctaTitle, eventBusTrigger } = step;
    const hasTriggerAndTitle = Boolean(eventBusTrigger && ctaTitle);

    if (hasTriggerAndTitle) {
      trackSweepstakesCtaClick(SWEEPSTAKES_NR_CTA.STEP_DRAWER_STANDARD_CTA, {
        stepIndex: step?.stepIndex,
        eventBusTrigger,
      });
      const payload = SWEEPSTAKES_CONSTANTS.EVENT_BUS.TRIGGER_PAYLOAD?.[eventBusTrigger] || {};
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger(eventBusTrigger, { ...payload });
    }
  };

  const infoText = sweepstakesData?.videoInfoText || null;
  const isVideoStep = videoConfig !== null;

  const handleBackToTracker = () => {
    if (windowExists()) {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger(
        'loyalty-benefits.navigate-sweepstakes-drawer',
        { drawerType: SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.DETAILS }
      );
    }
  };

  const handleVideoCtaClick = () => {
    trackSweepstakesCtaClick(SWEEPSTAKES_NR_CTA.STEP_DRAWER_SUBMIT_ENTRY, {
      stepIndex: step?.stepIndex,
      videoId: videoConfig?.videoId,
    });
    analyticsVideo(VIDEO_ANALYTICS.BUTTON_SUBMISSION, { id: videoConfig?.videoId });
    handleBackToTracker();
  };

  const content = (
    <div className="sui-space-y-4" data-testid="sweepstakes-step-content">
      <RenderDescription description={step.description} />
      <RenderStepVideo videoConfig={videoConfig} infoText={infoText} />
    </div>
  );

  const hasStandardCta = Boolean(
    step?.ctaTitle && (step?.eventBusTrigger || step?.ctaHref)
  );
  const hasVideoCta = Boolean(isVideoStep && step?.ctaTitle);

  let footer = null;
  if (hasVideoCta) {
    footer = (
      <RenderFooter
        showDrawerAppCta={step.showDrawerAppCta}
        ctaTitle={step.ctaTitle}
        onClick={handleVideoCtaClick}
        disabled={!(videoStarted || step?.completed)}
      />
    );
  } else if (isMobile && hasStandardCta) {
    footer = (
      <RenderFooter
        showDrawerAppCta={step.showDrawerAppCta}
        ctaTitle={step.ctaTitle}
        onClick={handleCtaClick}
      />
    );
  }

  return { content, footer };
};

StepContent.propTypes = {
  step: PropTypes.shape({
    id: PropTypes.string,
    stepIndex: PropTypes.number,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    description: PropTypes.string,
    ctaTitle: PropTypes.string,
    ctaHref: PropTypes.string,
    iconUrl: PropTypes.string,
    eventBusTrigger: PropTypes.string,
    showerDrawerAppCta: PropTypes.string,
    stepMetaData: PropTypes.shape({
      videoId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      brightcoveVideoId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      shouldUseContentPlayer: PropTypes.bool,
    }),
  }),
};

StepContent.defaultProps = {
  step: null,
};
